/**
 * slideshow-video-hold.js
 * ------------------------------------------------------------------------
 * Для авто-прокручиваемых слайдшоу (Slideshow: Full frame с autoplay):
 * пока показывается слайд с видео, автоперелистывание (3 с) ставится на
 * паузу, видео проигрывается ДО КОНЦА, затем слайдшоу листает дальше и
 * продолжает обычный ритм.
 *
 * Детали:
 *  - видео в теме рендерятся с loop — снимаем loop, иначе 'ended' не наступит;
 *  - при возврате на видео-слайд ролик стартует с начала;
 *  - паузим только то, что запаузили сами (не трогаем паузу темы от
 *    visibilitychange/hover);
 *  - слайдшоу без autoplay и слайды без видео не затрагиваются.
 *
 * Не модифицирует slideshow.js. Отдельный ассет + строка в theme.liquid.
 * ------------------------------------------------------------------------
 */
(() => {
  /** Слайдшоу, которые запаузили мы (а не тема). */
  const heldByUs = new WeakSet();

  /** Видимый <video> слайда (в слайде их может быть два: desktop и mobile). */
  const visibleVideo = (slide) =>
    [...slide.querySelectorAll('video')].find((v) => getComputedStyle(v).display !== 'none' && v.offsetWidth > 0);

  /**
   * @param {HTMLElement & {pause: Function, play: Function, next: Function, paused: boolean}} slideshow
   * @param {Element} slide
   */
  const onSlideShown = (slideshow, slide) => {
    const video = visibleVideo(slide);

    if (!video) {
      // Ушли с видео-слайда — возобновляем автоплей, если паузили его мы.
      if (heldByUs.has(slideshow)) {
        heldByUs.delete(slideshow);
        slideshow.play();
      }
      return;
    }

    slideshow.pause();
    heldByUs.add(slideshow);

    video.loop = false;
    // При повторном заходе на слайд — с начала.
    if (video.ended || video.currentTime > 0.1) video.currentTime = 0;
    video.play?.().catch?.(() => {});

    video.addEventListener(
      'ended',
      () => {
        // Пользователь мог уже улистать со слайда (в т.ч. на другой видео-слайд).
        if (!heldByUs.has(slideshow)) return;
        if (slideshow.slides?.[slideshow.current] !== slide) return;
        heldByUs.delete(slideshow);
        slideshow.play();
        slideshow.next(undefined, { animate: true });
      },
      { once: true }
    );
  };

  document.addEventListener('slideshow:select', (event) => {
    const slideshow = /** @type {any} */ (event.target);
    if (!slideshow?.hasAttribute?.('autoplay')) return;
    const slide = /** @type {CustomEvent} */ (event).detail?.slide;
    if (slide) onSlideShown(slideshow, slide);
  });

  // Первичная загрузка: первый слайд может быть видео (select-событие не шлётся).
  const init = () => {
    for (const slideshow of document.querySelectorAll('slideshow-component[autoplay]')) {
      const sl = /** @type {any} */ (slideshow);
      const slide = sl.slides?.[sl.current ?? 0] || slideshow.querySelector('slideshow-slide');
      if (slide) onSlideShown(sl, slide);
    }
  };

  // Ждём определения кастом-элемента, чтобы .slides/.current/.pause были доступны.
  customElements.whenDefined('slideshow-component').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  });
})();
